import { useState } from "react";
import { Loader2, Users, Shield, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUsers, type TenantUser } from "@/features/users/hooks/useUsers";
import { useRoles } from "@/features/roles/hooks/useRoles";

export function UsuariosPage() {
  const { users, isLoading, error, assignRole, removeRole } = useUsers();
  const { roles, isLoading: isLoadingRoles } = useRoles();
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRoleId) return;

    setIsAssigning(true);
    try {
      await assignRole(selectedUser.id, selectedRoleId);
      toast.success("Rol asignado correctamente");
      setSelectedRoleId("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al asignar rol");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveRole = async (userId: string, roleId: string, roleName: string) => {
    setIsRemoving(roleId);
    try {
      await removeRole(userId, roleId);
      toast.success(`Rol "${roleName}" removido`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al remover rol");
    } finally {
      setIsRemoving(null);
    }
  };

  // Get roles that the user doesn't have yet
  const getAvailableRoles = (user: TenantUser) => {
    const userRoleIds = user.roles.map((r) => r.id);
    return roles.filter((r) => !userRoleIds.includes(r.id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-destructive">
        Error: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Usuarios</h1>
        <p className="text-sm text-muted-foreground">
          Administre los usuarios y sus roles en su organizacion
        </p>
      </div>

      {/* Users Table */}
      <div className="border rounded-lg overflow-hidden bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground bg-muted/50">
              <th className="px-4 py-3 font-medium">Usuario</th>
              <th className="px-4 py-3 font-medium">Roles</th>
              <th className="px-4 py-3 font-medium">Miembro desde</th>
              <th className="px-4 py-3 font-medium w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.roles.length > 0 ? (
                      user.roles.map((role) => (
                        <Badge
                          key={role.id}
                          variant={role.isSystem ? "default" : "secondary"}
                          className="flex items-center gap-1"
                        >
                          <Shield className="h-3 w-3" />
                          {role.name}
                          <button
                            onClick={() => handleRemoveRole(user.id, role.id, role.name)}
                            disabled={isRemoving === role.id}
                            className="ml-1 hover:text-destructive disabled:opacity-50"
                            title="Remover rol"
                          >
                            {isRemoving === role.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                          </button>
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">Sin roles</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {new Date(user.joinedAt).toLocaleDateString("es-CL", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedUser(user)}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Rol
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay usuarios en este tenant</p>
        </div>
      )}

      {/* Assign Role Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Rol</DialogTitle>
            <DialogDescription>
              Asigne un nuevo rol a {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedUser && (
              <>
                <div className="text-sm">
                  <span className="text-muted-foreground">Roles actuales: </span>
                  {selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((r) => r.name).join(", ")
                  ) : (
                    <span className="text-muted-foreground">Ninguno</span>
                  )}
                </div>

                <Select
                  value={selectedRoleId}
                  onValueChange={setSelectedRoleId}
                  disabled={isLoadingRoles}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles(selectedUser).map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          {role.name}
                        </div>
                      </SelectItem>
                    ))}
                    {getAvailableRoles(selectedUser).length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        El usuario ya tiene todos los roles disponibles
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSelectedUser(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAssignRole}
              disabled={!selectedRoleId || isAssigning}
            >
              {isAssigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Asignar Rol
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
